import type { ReactNode } from 'react';
import { ProfileCard } from '@/components/common/ProfileCard';
import { ProfileFact, ProfileFacts } from '@/components/common/ProfileFacts';
import { ProfileChipList } from '@/components/common/ProfileChip';
import { formatDateOnly } from '@/lib/utils/date.utils';
import {
  formatCompactCurrencyRange,
  formatCurrency,
  humanizeToken,
  optionLabel,
  optionLabels,
} from '@/lib/utils/format.utils';
import {
  AI_CLASSIFICATION_OPTIONS,
  FUNDING_STAGE_OPTIONS,
  INSTRUMENT_SEGMENT_OPTIONS,
  INVESTOR_TYPE_OPTIONS,
  MARKET_MODEL_OPTIONS,
  PAYING_CUSTOMERS_OPTIONS,
  REVENUE_GROWTH_RATE_OPTIONS,
  REVENUE_MODEL_OPTIONS,
  REVENUE_RANGE_OPTIONS,
} from '@/features/startups/constants';
import type { StartupProfileData } from '../../api/startup-profile.api';
import { STARTUP_PROFILE_STRINGS as T } from './startup-profile.strings';

export interface StartupCardProps {
  data: StartupProfileData;
  /** The card's Edit button; omitted for read-only viewers (FR-169). */
  action?: ReactNode;
  /** Opens this card's panel from an inline "Add" prompt; omitted when read-only. */
  onAdd?: () => void;
}

function fundingStageLabel(value: string | null | undefined): string | undefined {
  if (!value) return undefined;
  return optionLabel(FUNDING_STAGE_OPTIONS, value) ?? humanizeToken(value);
}

function leadInvestor(data: StartupProfileData): string {
  if (!data.hasLeadInvestor) return T.LEAD_NO;
  return data.leadInvestorName || T.LEAD_CONFIRMED;
}

/** Company details (mockup: 5 of 12 columns, facts two to a row). */
export function CompanyDetailsCard({ data, action }: StartupCardProps) {
  const marketModels = data.marketModels ?? [];
  return (
    <ProfileCard title={T.COMPANY_DETAILS} action={action}>
      {/* Only for someone who can edit: the logo is uploaded from this
          section's Edit panel, which admins could not find (UX round 3,
          StartupAdminPatty P34). */}
      {action && <p className="text-subtext mb-4 text-sm">{T.COMPANY_DETAILS_LOGO_HINT}</p>}
      <ProfileFacts columns={2}>
        <ProfileFact label={T.FUNDING_STAGE} value={fundingStageLabel(data.fundingStage)} />
        <ProfileFact label={T.YEAR_FOUNDED} value={data.yearFounded} />
        <ProfileFact label={T.EMPLOYEES} value={data.employeeCount} />
        <ProfileFact label={T.REGION} value={data.hqWorldRegion} />
        <ProfileFact
          label={T.MARKET_MODEL}
          value={optionLabels(MARKET_MODEL_OPTIONS, marketModels)}
        />
        <ProfileFact
          label={T.AI_CLASSIFICATION}
          value={optionLabel(AI_CLASSIFICATION_OPTIONS, data.aiClassification)}
        />
        <ProfileFact label={T.RECENT_MILESTONE} value={data.recentMilestone} span={2} />
      </ProfileFacts>
    </ProfileCard>
  );
}

/** Current raise (mockup: 7 of 12 columns, facts four to a row). */
export function CurrentRaiseCard({ data, action }: StartupCardProps) {
  const investorTypes = (data.investorTypesSought ?? []).map(
    (value) => optionLabel(INVESTOR_TYPE_OPTIONS, value) ?? value,
  );
  return (
    <ProfileCard title={T.CURRENT_RAISE} action={action}>
      <ProfileFacts columns={4}>
        <ProfileFact
          label={T.ROUND_SIZE}
          value={formatCompactCurrencyRange(data.financeSoughtLow, data.financeSoughtHigh)}
        />
        <ProfileFact
          label={T.INSTRUMENT}
          value={optionLabel(INSTRUMENT_SEGMENT_OPTIONS, data.instrument) ?? data.instrument}
        />
        <ProfileFact label={T.VALUATION_OR_CAP} value={formatCurrency(data.valuationOrCap)} />
        <ProfileFact label={T.TARGET_CLOSE} value={formatDateOnly(data.targetCloseDate, '')} />
        <ProfileFact label={T.LEAD_INVESTOR} value={leadInvestor(data)} />
        <ProfileFact label={T.INVESTOR_TYPES_SOUGHT} span={3}>
          {investorTypes.length > 0 ? (
            <div className="mt-1">
              <ProfileChipList items={investorTypes} />
            </div>
          ) : undefined}
        </ProfileFact>
        <ProfileFact label={T.USE_OF_FUNDS} value={data.useOfFunds} span={4} prose />
      </ProfileFacts>
    </ProfileCard>
  );
}

/** Financials: the numbers the header strip does not carry, four to a row across the page. */
export function FinancialsCard({ data, action }: StartupCardProps) {
  return (
    <ProfileCard title={T.FINANCIALS} action={action}>
      <ProfileFacts columns={4}>
        <ProfileFact
          label={T.TOTAL_REVENUE}
          value={optionLabel(REVENUE_RANGE_OPTIONS, data.totalRevenueRange)}
        />
        <ProfileFact label={T.ARR} value={formatCurrency(data.arr)} />
        <ProfileFact label={T.RAISED_TO_DATE} value={formatCurrency(data.amountRaisedToDate)} />
        <ProfileFact
          label={T.NONDILUTIVE_FUNDING}
          value={formatCurrency(data.nondilutiveFunding)}
        />
        <ProfileFact
          label={T.REVENUE_MODEL}
          value={optionLabel(REVENUE_MODEL_OPTIONS, data.revenueModel)}
        />
        <ProfileFact
          label={T.REVENUE_GROWTH}
          value={optionLabel(REVENUE_GROWTH_RATE_OPTIONS, data.revenueGrowthRate)}
        />
        <ProfileFact
          label={T.PAYING_CUSTOMERS}
          value={optionLabel(PAYING_CUSTOMERS_OPTIONS, data.payingCustomers)}
        />
      </ProfileFacts>
    </ProfileCard>
  );
}
